import { FC } from "react";
import { Content, isFilled } from "@prismicio/client";
import { SliceComponentProps } from "@prismicio/react";
import { appLocales, getPrismicLocale, Locales } from "@/lib/i18n";
import Link from "next/link";
import { __, getPermalink } from "@/lib/i18n/functions";
import { getAllByIDs, getAllByUids, getByID } from "@/lib/prismic";
import { ProductDocument, TablesDocument } from "@/prismicio-types";
import CollectionSlider from "@/components/molecules/collection-slider";
import ProductsGridClient from "@/components/molecules/products-grid-client";
import { getRelatedPrismicProductCategories } from "@/lib/prismic/products";

export type ProductsAndTablesListProps = SliceComponentProps<Content.ProductsAndTablesListSlice> & {
  context?: { locale?: Locales };
};

const ProductsAndTablesList: FC<ProductsAndTablesListProps> = async ({ slice, context }) => {
  const locale = context?.locale || appLocales.defaultLocale;

  const { title, link_label, table, products, background_color } = slice.primary;

  const productRefs = products.map((item) => item.product).filter(isFilled.contentRelationship);
  const ids = productRefs?.map((p) => p.id);
  let prismicProducts: ProductDocument[] | null = null;

  prismicProducts = (await getAllByIDs(ids, { lang: getPrismicLocale(locale) })) as ProductDocument[] | null;

  const productUIDs =
    prismicProducts
      ?.map((product) => product.alternate_languages.find((alt) => alt.lang === getPrismicLocale("fr"))?.uid)
      .filter((uid): uid is string => typeof uid === "string") || [];

  const frenchProducts = (await getAllByUids("product", productUIDs, "fr")) as ProductDocument[] | null;

  if (locale !== "fr") {
    prismicProducts = prismicProducts
      ?.map((localeProduct) => {
        const frenchEquivalent = frenchProducts?.find(
          (frenchProduct) =>
            frenchProduct.alternate_languages.find((alt) => alt.lang === getPrismicLocale(locale))?.uid ===
            localeProduct.uid
        );
        if (frenchEquivalent) {
          return {
            ...localeProduct,
            data: {
              ...localeProduct.data,
              images: frenchEquivalent.data.images,
              featuredImage: frenchEquivalent.data.featuredImage,
              productCollection: frenchEquivalent.data.productCollection,
              image_full_size_card: frenchEquivalent.data.image_full_size_card,
            },
          };
        }
        return undefined;
      })
      .filter(Boolean) as ProductDocument[];
  }

  if (!prismicProducts || !prismicProducts.length) return null;

  const productCategories = await getRelatedPrismicProductCategories(
    (locale === "fr" ? prismicProducts : frenchProducts) ?? [],
    locale
  );

  const tableDoc = isFilled.contentRelationship(table)
    ? ((await getByID(table.id, { lang: getPrismicLocale(locale) })) as TablesDocument)
    : null;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      aria-labelledby={__("Tables and Products", locale)}
      className="container space-y-8 px-4 py-6 md:px-7.5 md:py-12"
      style={{ backgroundColor: background_color || "" }}
    >
      <header className="flex flex-col items-center justify-between gap-3 md:flex-row">
        {title && <h2 className="font-cabinet-grotesk text-center text-3xl font-medium">{title}</h2>}
        {link_label && (
          <Link
            className="text-dark-primary focus-visible:ring-dark-primary/70 rounded-sm text-base font-medium underline focus-visible:ring-[3px] focus-visible:ring-offset-3 focus-visible:outline-none md:uppercase"
            href={tableDoc?.uid ? getPermalink(locale, "tables") : getPermalink(locale, "catalogue")}
          >
            {link_label}
          </Link>
        )}
      </header>
      <div className="flex flex-col gap-3 md:gap-5 lg:flex-row lg:items-start">
        {tableDoc && (
          <CollectionSlider
            locale={locale}
            className="hidden md:block"
            collections={
              tableDoc.data.featured_image.url
                ? [
                    {
                      image: tableDoc.data.featured_image.url,
                      link: `${getPermalink(locale, "tables")}/${tableDoc.uid}`,
                      tag: __("See used items", locale),
                    },
                  ]
                : []
            }
          />
        )}
        <ProductsGridClient
          products={prismicProducts}
          locale={locale}
          collectionCategories={productCategories}
          className={tableDoc ? "lg:basis-[66%] lg:grid-cols-2 xl:basis-[50%]" : "lg:grid-cols-4"}
        />
      </div>
    </section>
  );
};

export default ProductsAndTablesList;
